import { TeamCalendarPage } from './pages/TeamCalendarPage';
import { lazy, Suspense, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router';
import { useAuth } from './auth/AuthProvider';
import { Logo } from './components/Brand';
import { Layout } from './components/Layout';
import { useI18n } from './lib/i18n';
import { supabaseConfigured } from './lib/supabase';
const AuditPage = lazy(() => import('./pages/AuditPage').then((m) => ({ default: m.AuditPage })));
const CartellinoPage = lazy(() => import('./pages/CartellinoPage').then((m) => ({ default: m.CartellinoPage })));
const CommunicationsPage = lazy(() => import('./pages/CommunicationsPage').then((m) => ({ default: m.CommunicationsPage })));
const CompensationPage = lazy(() => import('./pages/CompensationPage').then((m) => ({ default: m.CompensationPage })));
const EmployeesPage = lazy(() => import('./pages/EmployeesPage').then((m) => ({ default: m.EmployeesPage })));
const KioskPage = lazy(() => import('./pages/KioskPage').then((m) => ({ default: m.KioskPage })));
const LoginPage = lazy(() => import('./pages/LoginPage').then((m) => ({ default: m.LoginPage })));
const PayrollPage = lazy(() => import('./pages/PayrollPage').then((m) => ({ default: m.PayrollPage })));
const PresencePage = lazy(() => import('./pages/PresencePage').then((m) => ({ default: m.PresencePage })));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage').then((m) => ({ default: m.PrivacyPage })));
const RegisterCompanyPage = lazy(() => import('./pages/RegisterCompanyPage').then((m) => ({ default: m.RegisterCompanyPage })));
const RequestsPage = lazy(() => import('./pages/RequestsPage').then((m) => ({ default: m.RequestsPage })));
const SettingsPage = lazy(() => import('./pages/SettingsPage').then((m) => ({ default: m.SettingsPage })));
const SitesPage = lazy(() => import('./pages/SitesPage').then((m) => ({ default: m.SitesPage })));
const OrganizationPage = lazy(() => import('./pages/OrganizationPage').then(m=>({default:m.OrganizationPage})));

function Message({ title, body, action }: { title: string; body: string; action?: React.ReactNode }) {
  return (
    <div className="center">
      <div className="card auth-card">
        <span style={{ background: 'var(--slate)', borderRadius: 14, padding: 10, alignSelf: 'flex-start', display: 'inline-flex' }}>
          <Logo size={32} />
        </span>
        <h1>{title}</h1>
        <p className="muted">{body}</p>
        {action}
      </div>
    </div>
  );
}

function Portal() {
  const { t } = useI18n();
  const { ready, session, memberships, membershipsLoading, isHr, isManager, signOut } = useAuth();
  const [registering, setRegistering] = useState(false);

  if (!ready || (session && membershipsLoading)) {
    return <div className="center muted">{t('common.loading')}</div>;
  }
  if (!session) return <LoginPage />;
  if (memberships.length === 0) {
    if (registering) return <RegisterCompanyPage onCancel={() => setRegistering(false)} />;
    return (
      <Message
        title={t('access.noCompanyTitle')}
        body={t('access.noCompanyBody')}
        action={
          <div className="row">
            <button className="btn btn-primary" onClick={() => setRegistering(true)}>
              {t('access.registerCompany')}
            </button>
            <button className="btn" onClick={signOut}>
              {t('common.logout')}
            </button>
          </div>
        }
      />
    );
  }
  if (!isHr && !isManager) {
    return (
      <Message
        title={t('access.employeeTitle')}
        body={t('access.employeeBody')}
        action={
          <button className="btn" onClick={signOut}>
            {t('common.logout')}
          </button>
        }
      />
    );
  }

  return (
    <Routes>
      <Route element={<Layout />}>
        <Route index element={<PresencePage />} />
        <Route path="cartellino" element={<CartellinoPage />} />
        <Route path="richieste" element={<RequestsPage />} />
        <Route path="calendario" element={<TeamCalendarPage />} />
        <Route path="impostazioni" element={<SettingsPage managerOnly={!isHr} />} />
        {/* A team manager sees presence, cartellino, requests, calendar and their own passkeys; the rest is HR's. */}
        {isHr ? hrRoutes() : null}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

function hrRoutes() {
  return (
    <>
        <Route path="comunicazioni" element={<CommunicationsPage />} />
        <Route path="dipendenti" element={<EmployeesPage />} />
        <Route path="organizzazione" element={<OrganizationPage />} />
        <Route path="sedi" element={<SitesPage />} />
        <Route
          path="cedolini"
          element={<PayrollPage />}
        />
        <Route path="retribuzione" element={<CompensationPage />} />
        <Route path="privacy" element={<PrivacyPage />} />
        <Route path="registro" element={<AuditPage />} />
    </>
  );
}

export function App() {
  const { t } = useI18n();
  if (!supabaseConfigured) return <Message title={t('config.missingTitle')} body={t('config.missingBody')} />;
  return (
    <Suspense fallback={<div className="center muted">{t('common.loading')}</div>}>
      <BrowserRouter>
        <Routes>
          {/* The kiosk tablet has no user session. */}
          <Route path="/kiosk" element={<KioskPage />} />
          <Route path="/*" element={<Portal />} />
        </Routes>
      </BrowserRouter>
    </Suspense>
  );
}
